import React, { useEffect, useMemo, useState } from "react";
import { Tag, message } from "antd";
import { RiseOutlined, FallOutlined } from "@ant-design/icons";
import { MdPeople, MdInbox, MdAssignment, MdCampaign, MdAllInclusive } from "react-icons/md";
import dayjs from "dayjs";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { getShippedOrdersByDateRange, getOtcBreakEvenByMonth } from "../../utils/API";

// OTC snapshot helpers — keep parity with src/Pages/otc/otc.tsx
const formatCurrencyWhole = (value: number) =>
  `$${Math.round(Number(value) || 0).toLocaleString()}`;

// Cards show N/A instead of a bare $0 when there is no value.
const orNA = (value: number) =>
  Math.round(Number(value) || 0) === 0 ? "N/A" : formatCurrencyWhole(value);

const parseOtcCurrencyValue = (value: any) => {
  const normalized = String(value ?? "").replace(/[$,]/g, "");
  return Number.parseFloat(normalized) || 0;
};

const isTestUnlimited = (leadFrom: any) => {
  const v = String(leadFrom || "").trim().toLowerCase();
  return v === "test unlimited" || v === "testunl" || v === "testunlimited";
};

const isAdwords = (leadFrom: any) => {
  const v = String(leadFrom || "").trim().toLowerCase();
  return v === "addwords" || v === "adwords";
};

const normalizeLeadName = (leadFrom: any) => {
  const raw = String(leadFrom || "Other").trim() || "Other";
  return raw.toLowerCase() === "ebay" ? "EBay" : raw;
};

const LEAD_COLORS = [
  "#004aad",
  "#4FD1C5",
  "#FFB347",
  "#8B8BFF",
  "#F6779A",
  "#32E63B",
  "#9CA3AF",
];

type OtcPeriod = "today" | "week" | "month" | "year" | "custom";

type CustomRange = [dayjs.Dayjs, dayjs.Dayjs] | null;

const getPeriodRange = (period: OtcPeriod, custom: CustomRange = null) => {
  const now = dayjs();
  switch (period) {
    case "today":
      return { start: now.startOf("day"), end: now.endOf("day") };
    case "week":
      return { start: now.startOf("week"), end: now.endOf("week") };
    case "year":
      return { start: now.startOf("year"), end: now.endOf("year") };
    case "custom":
      // Falls back to the default period until a range is actually picked.
      return custom
        ? { start: custom[0].startOf("day"), end: custom[1].endOf("day") }
        : { start: now.startOf("year"), end: now.endOf("year") };
    case "month":
    default:
      return { start: now.startOf("month"), end: now.endOf("month") };
  }
};

const PERIOD_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
  { value: "year", label: "This Year" },
  { value: "custom", label: "Custom Range" },
];

const getPeriodLabel = (period: OtcPeriod, custom: CustomRange = null) => {
  if (period !== "custom") {
    return (
      PERIOD_OPTIONS.find((option) => option.value === period)?.label || "This Month"
    );
  }
  if (!custom) return "Custom Range";
  return `${custom[0].format("MM/DD/YYYY")} – ${custom[1].format("MM/DD/YYYY")}`;
};

// Day buckets read fine up to ~2 months; wider custom ranges roll up to months.
const shouldBucketByMonth = (
  period: OtcPeriod,
  start: dayjs.Dayjs,
  end: dayjs.Dayjs,
) => period === "year" || end.diff(start, "day") > 62;

// The comparison window for a trend: the equivalent period immediately before this one.
// A custom range compares against the equally long window right before it.
const getPreviousRange = (
  period: OtcPeriod,
  start: dayjs.Dayjs,
  end: dayjs.Dayjs,
) => {
  if (period === "custom") {
    const spanDays = end.diff(start, "day") + 1;
    const prevEnd = start.subtract(1, "day").endOf("day");
    return { start: prevEnd.subtract(spanDays - 1, "day").startOf("day"), end: prevEnd };
  }
  const unit = (period === "today" ? "day" : period) as any;
  const prevStart = start.subtract(1, unit).startOf(unit);
  return { start: prevStart, end: prevStart.endOf(unit) };
};

const getCompareLabel = (period: OtcPeriod) => {
  switch (period) {
    case "today":
      return "vs yesterday";
    case "week":
      return "vs last week";
    case "year":
      return "vs last year";
    case "custom":
      return "vs previous period";
    case "month":
    default:
      return "vs last month";
  }
};

interface OtcRow {
  rawDate: string | null;
  gm: number;
  gs: number;
  leadFrom: string;
  status: string;
}

// Orders still in "Incoming" status haven't landed yet, so Gross Sales skips them.
const isIncomingStatus = (status: string) => /incoming/i.test(status);

const fetchOtcRows = async (
  start: dayjs.Dayjs,
  end: dayjs.Dayjs,
): Promise<OtcRow[]> => {
  const rangeStart = start.format("YYYY-MM-DD");
  const rangeEnd = end.format("YYYY-MM-DD");

  // The shipped endpoint is month scoped, so request each month the range spans.
  const months: dayjs.Dayjs[] = [];
  let cursor = start.startOf("month");
  const lastMonth = end.startOf("month");
  while (cursor.isBefore(lastMonth) || cursor.isSame(lastMonth, "month")) {
    months.push(cursor);
    cursor = cursor.add(1, "month");
  }

  const responses = await Promise.all(
    months.map((month) =>
      getShippedOrdersByDateRange(
        month.startOf("month").format("YYYY-MM-DD"),
        month.endOf("month").format("YYYY-MM-DD"),
      ).catch(() => ({ data: [] })),
    ),
  );

  return responses
    .flatMap((res: any) => (Array.isArray(res?.data) ? res.data : []))
    .map((item: any) => ({
      rawDate: item.date ? dayjs(item.date).format("YYYY-MM-DD") : null,
      gm: parseOtcCurrencyValue(item.gross_margin),
      gs: parseOtcCurrencyValue(item.gross_sale),
      leadFrom: normalizeLeadName(item.lead_from),
      status: String(item.po_status || "").trim(),
    }))
    .filter((row) =>
      row.rawDate ? row.rawDate >= rangeStart && row.rawDate <= rangeEnd : false,
    );
};

const sumBreakEven = async (start: dayjs.Dayjs, end: dayjs.Dayjs) => {
  const months: dayjs.Dayjs[] = [];
  let cursor = start.startOf("month");
  const lastMonth = end.startOf("month");
  while (cursor.isBefore(lastMonth) || cursor.isSame(lastMonth, "month")) {
    months.push(cursor);
    cursor = cursor.add(1, "month");
  }

  const values = await Promise.all(
    months.map((month) =>
      getOtcBreakEvenByMonth(month.month() + 1, month.year())
        .then((res: any) => {
          const monthStart = month.startOf("month");
          const monthEnd = month.endOf("month");
          // Prorate by the days of this month actually inside [start, end] — a
          // custom range covering only part of a month shouldn't be judged
          // against that month's full break-even target.
          const coveredStart = start.isAfter(monthStart) ? start : monthStart;
          const coveredEnd = end.isBefore(monthEnd) ? end : monthEnd;
          const coveredDays = coveredEnd.diff(coveredStart, "day") + 1;
          const totalDaysInMonth = monthEnd.diff(monthStart, "day") + 1;
          const fraction = coveredDays / totalDaysInMonth;
          return (Number(res?.breakEvenValue) || 0) * fraction;
        })
        .catch(() => 0),
    ),
  );

  return values.reduce((sum, value) => sum + value, 0);
};

// OTC snapshot trackers moved over from the old Dashboard page: the five stat
// cards, Gross Margin Over time and Lead-wise Gross Margin.
const OtcTrackers = ({
  period,
  customRange,
  allStart,
  aside,
}: {
  period: string;
  customRange: [string, string] | null;
  allStart?: string | null;
  aside?: (gmPercent: number) => React.ReactNode;
}) => {
  // The page's top timeline drives everything here. "All Time" has no fixed
  // window, so it runs as a custom range from the first data date until today.
  const cardPeriod: OtcPeriod =
    period === "all" || period === "custom" ? "custom" : (period as OtcPeriod);
  const cardCustomRange: CustomRange = useMemo(() => {
    if (period === "all") {
      return [allStart ? dayjs(allStart) : dayjs().subtract(3, "year").startOf("year"), dayjs()];
    }
    if (period === "custom" && customRange) return [dayjs(customRange[0]), dayjs(customRange[1])];
    return null;
  }, [period, customRange, allStart]);
  const revenuePeriod = cardPeriod;
  const revenueCustomRange = cardCustomRange;
  const statusPeriod = cardPeriod;
  const statusCustomRange = cardCustomRange;
  const periodLabel = period === "all" ? "All Time" : getPeriodLabel(cardPeriod, cardCustomRange);
  const [otcCardRows, setOtcCardRows] = useState<OtcRow[]>([]);
  const [otcPrevGm, setOtcPrevGm] = useState(0);
  const [otcPrevGs, setOtcPrevGs] = useState(0);
  const [otcPrevAdwordsGm, setOtcPrevAdwordsGm] = useState(0);
  const [otcPrevTestUnlimitedGm, setOtcPrevTestUnlimitedGm] = useState(0);
  const [otcBreakEven, setOtcBreakEven] = useState(0);
  const [gmTrendRows, setGmTrendRows] = useState<OtcRow[]>([]);
  const [gmTrendPrevGm, setGmTrendPrevGm] = useState(0);
  const [leadRows, setLeadRows] = useState<OtcRow[]>([]);


  // Stat cards — OTC snapshot totals for the selected period + previous period for trend
  useEffect(() => {
    if (cardPeriod === "custom" && !cardCustomRange) return;

    const loadOtcCards = async () => {
      try {
        const { start, end } = getPeriodRange(cardPeriod, cardCustomRange);
        const prev = getPreviousRange(cardPeriod, start, end);

        const [rows, prevRows, breakEven] = await Promise.all([
          fetchOtcRows(start, end),
          fetchOtcRows(prev.start, prev.end),
          sumBreakEven(start, end),
        ]);

        setOtcCardRows(rows);
        setOtcPrevGm(prevRows.reduce((sum, row) => sum + row.gm, 0));
        setOtcPrevGs(
          prevRows.reduce((sum, row) => (isIncomingStatus(row.status) ? sum : sum + row.gs), 0),
        );
        setOtcPrevAdwordsGm(
          prevRows.reduce((sum, row) => (isAdwords(row.leadFrom) ? sum + row.gm : sum), 0),
        );
        setOtcPrevTestUnlimitedGm(
          prevRows.reduce((sum, row) => (isTestUnlimited(row.leadFrom) ? sum + row.gm : sum), 0),
        );
        setOtcBreakEven(breakEven);
      } catch (error: any) {
        message.error(
          error?.response?.data?.message || "Failed to load OTC snapshot totals",
        );
      }
    };
    loadOtcCards();
  }, [cardPeriod, cardCustomRange]);

  // Gross Margin over time — OTC snapshot GM bucketed across the selected period
  useEffect(() => {
    if (revenuePeriod === "custom" && !revenueCustomRange) return;

    const loadGmTrend = async () => {
      try {
        const { start, end } = getPeriodRange(revenuePeriod, revenueCustomRange);
        const prev = getPreviousRange(revenuePeriod, start, end);

        const [rows, prevRows] = await Promise.all([
          fetchOtcRows(start, end),
          fetchOtcRows(prev.start, prev.end),
        ]);

        setGmTrendRows(rows);
        setGmTrendPrevGm(prevRows.reduce((sum, row) => sum + row.gm, 0));
      } catch (error: any) {
        message.error(
          error?.response?.data?.message || "Failed to load gross margin trend",
        );
      }
    };
    loadGmTrend();
  }, [revenuePeriod, revenueCustomRange]);

  // Lead-wise GM breakdown — same grouping the OTC snapshot sidebar uses
  useEffect(() => {
    if (statusPeriod === "custom" && !statusCustomRange) return;

    const loadLeadBreakdown = async () => {
      try {
        const { start, end } = getPeriodRange(statusPeriod, statusCustomRange);
        setLeadRows(await fetchOtcRows(start, end));
      } catch (error: any) {
        message.error(
          error?.response?.data?.message || "Failed to load lead breakdown",
        );
      }
    };
    loadLeadBreakdown();
  }, [statusPeriod, statusCustomRange]);

  const percentChange = (current: number, previous: number) => {
    if (!previous) return current ? 100 : 0;
    return ((current - previous) / Math.abs(previous)) * 100;
  };

  const totalGM = useMemo(
    () => otcCardRows.reduce((sum, row) => sum + row.gm, 0),
    [otcCardRows],
  );
  const totalGS = useMemo(
    () =>
      otcCardRows.reduce(
        (sum, row) => (isIncomingStatus(row.status) ? sum : sum + row.gs),
        0,
      ),
    [otcCardRows],
  );
  const testUnlimitedGM = useMemo(
    () =>
      otcCardRows.reduce(
        (sum, row) => (isTestUnlimited(row.leadFrom) ? sum + row.gm : sum),
        0,
      ),
    [otcCardRows],
  );
  const adwordsGM = useMemo(
    () =>
      otcCardRows.reduce(
        (sum, row) => (isAdwords(row.leadFrom) ? sum + row.gm : sum),
        0,
      ),
    [otcCardRows],
  );
  const currentPositionValue = totalGM - otcBreakEven;
  const gmPercent = totalGS ? (totalGM / totalGS) * 100 : 0;

  const gmTrend = percentChange(totalGM, otcPrevGm);
  const gmTrendIsUp = gmTrend >= 0;
  const gmTrendText = `${Math.abs(gmTrend).toFixed(1)}%`;

  const gsTrend = percentChange(totalGS, otcPrevGs);
  const gsTrendIsUp = gsTrend >= 0;
  const gsTrendText = `${Math.abs(gsTrend).toFixed(1)}%`;

  const adwordsTrend = percentChange(adwordsGM, otcPrevAdwordsGm);
  const adwordsTrendIsUp = adwordsTrend >= 0;
  const adwordsTrendText = `${Math.abs(adwordsTrend).toFixed(1)}%`;

  const testUnlimitedTrend = percentChange(testUnlimitedGM, otcPrevTestUnlimitedGm);
  const testUnlimitedTrendIsUp = testUnlimitedTrend >= 0;
  const testUnlimitedTrendText = `${Math.abs(testUnlimitedTrend).toFixed(1)}%`;

  const compareLabel = getCompareLabel(cardPeriod);

  // Bucket GM by day for short periods, by month for the year / wide custom ranges
  const revenueBucketByMonth = useMemo(() => {
    const { start, end } = getPeriodRange(revenuePeriod, revenueCustomRange);
    return shouldBucketByMonth(revenuePeriod, start, end);
  }, [revenuePeriod, revenueCustomRange]);

  const lineData = useMemo(() => {
    const { start, end } = getPeriodRange(revenuePeriod, revenueCustomRange);
    const byMonth = revenueBucketByMonth;
    const buckets: Array<{ name: string; key: string; gm: number }> = [];

    let cursor = byMonth ? start.startOf("month") : start.startOf("day");
    const last = byMonth ? end.startOf("month") : end.startOf("day");
    while (cursor.isBefore(last) || cursor.isSame(last, byMonth ? "month" : "day")) {
      buckets.push({
        name: byMonth
          ? cursor.format("MMM YY")
          : revenuePeriod === "custom"
            ? cursor.format("MM/DD")
            : cursor.format("D"),
        key: cursor.format(byMonth ? "YYYY-MM" : "YYYY-MM-DD"),
        gm: 0,
      });
      cursor = cursor.add(1, byMonth ? "month" : "day");
    }

    const index = new Map(buckets.map((bucket) => [bucket.key, bucket]));
    gmTrendRows.forEach((row) => {
      if (!row.rawDate) return;
      const key = byMonth ? row.rawDate.slice(0, 7) : row.rawDate;
      const bucket = index.get(key);
      if (bucket) bucket.gm += row.gm;
    });

    return buckets.map(({ name, gm }) => ({ name, gm }));
  }, [gmTrendRows, revenuePeriod, revenueCustomRange, revenueBucketByMonth]);

  const totalTrendGM = useMemo(
    () => lineData.reduce((sum, item) => sum + Number(item.gm || 0), 0),
    [lineData],
  );

  // The chart's own trend — driven by the chart's period, not the stat-card period
  const chartTrend = percentChange(totalTrendGM, gmTrendPrevGm);
  const chartTrendIsUp = chartTrend >= 0;
  const chartTrendText = `${Math.abs(chartTrend).toFixed(1)}%`;
  const chartCompareLabel = getCompareLabel(revenuePeriod);

  // Lead-wise GM totals — mirrors the OTC snapshot sidebar grouping
  const pieData = useMemo(() => {
    const totals = leadRows.reduce<Record<string, number>>((acc, row) => {
      acc[row.leadFrom] = (acc[row.leadFrom] || 0) + row.gm;
      return acc;
    }, {});

    return Object.entries(totals)
      .filter(([, value]) => value > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([name, value], index) => ({
        name,
        value: Math.round(value),
        color: LEAD_COLORS[index % LEAD_COLORS.length],
      }));
  }, [leadRows]);

  // Order count per lead source over the same period (OTC shipped orders).
  const leadCounts = useMemo(() => {
    const counts = leadRows.reduce<Record<string, number>>((acc, row) => {
      acc[row.leadFrom] = (acc[row.leadFrom] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([name, count], index) => ({
        name,
        count,
        color: LEAD_COLORS[index % LEAD_COLORS.length],
      }));
  }, [leadRows]);
  const totalLeadCount = leadCounts.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="space-y-4 mb-4">

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
        <StatCard
          title="Addwords GM"
          periodLabel={periodLabel}
          value={orNA(adwordsGM)}
          trend={adwordsTrendText}
          trendText={compareLabel}
          isUp={adwordsTrendIsUp}
          icon={<MdCampaign />}
          iconBg="bg-[#FFF5E6]"
          iconColor="text-[#FFB347]"
        />
        <StatCard
          title="Test Unlimited GM"
          periodLabel={periodLabel}
          value={orNA(testUnlimitedGM)}
          trend={testUnlimitedTrendText}
          trendText={compareLabel}
          isUp={testUnlimitedTrendIsUp}
          icon={<MdAllInclusive />}
          iconBg="bg-[#E6F9F4]"
          iconColor="text-[#4FD1C5]"
        />
        <StatCard
          title="Gross Sales"
          periodLabel={periodLabel}
          value={orNA(totalGS)}
          trend={gsTrendText}
          trendText={compareLabel}
          isUp={gsTrendIsUp}
          icon={<MdInbox />}
          iconBg="bg-[#FFF5E6]"
          iconColor="text-[#FFB347]"
        />
        <StatCard
          title="Gross Margin"
          periodLabel={periodLabel}
          value={orNA(totalGM)}
          trend={gmTrendText}
          trendText={compareLabel}
          isUp={gmTrendIsUp}
          icon={<MdPeople />}
          iconBg="bg-[#EBEBFF]"
          iconColor="text-[#8B8BFF]"
        />
        <StatCard
          title="Current Position"
          periodLabel={periodLabel}
          value={orNA(currentPositionValue)}
          icon={<MdAssignment />}
          iconBg="bg-[#EBEBFF]"
          iconColor="text-[#8B8BFF]"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white p-6 rounded-[24px] border border-gray-100 shadow-sm">
          <div className="flex justify-between items-start gap-3 mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-700">
                Gross Margin Over time
              </h3>
              <p className="text-xs text-gray-400">
                {formatCurrencyWhole(totalTrendGM)}
                <Tag
                  color={chartTrendIsUp ? "green" : "red"}
                  className={`text-[10px] border-none mx-2 ${
                    chartTrendIsUp
                      ? "bg-green-50 text-green-600"
                      : "bg-red-50 text-red-600"
                  }`}
                >
                  {chartTrendText} {chartTrendIsUp ? "up" : "down"}
                </Tag>
                <span className="mr-1">{chartCompareLabel}</span>·
                <span className="ml-1">
                  {revenueBucketByMonth ? "GM by month" : "GM by day"}
                </span>
              </p>
            </div>
          </div>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={lineData}>
                <defs>
                  <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#004aad" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#004aad" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#9ca3af" }} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#9ca3af" }} />
                <Tooltip formatter={(value: any) => formatCurrencyWhole(Number(value))} />
                <Area
                  type="monotone"
                  dataKey="gm"
                  name="Gross Margin"
                  stroke="#004aad"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorRev)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white p-6 rounded-[24px] border border-gray-100 shadow-sm flex flex-col">
          <div className="flex justify-between items-center gap-2 mb-4">
            <h3 className="text-lg font-semibold text-gray-700">
              Lead-wise Gross Margin
            </h3>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={pieData} innerRadius={80} outerRadius={100} paddingAngle={5} dataKey="value">
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: any) => formatCurrencyWhole(Number(value))} />
              </PieChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-3 gap-4 w-full mt-6 text-center border-t pt-4">
              {pieData.map((item) => (
                <div key={item.name}>
                  <p className="text-xs text-gray-400 flex items-center justify-center gap-1">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }}></span>
                    {item.name}
                  </p>
                  <p className="text-lg font-bold text-gray-700">
                    {formatCurrencyWhole(item.value)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
        {aside ? <div>{aside(gmPercent)}</div> : null}
        <div className="lg:col-span-2 bg-white p-6 rounded-[24px] border border-gray-100 shadow-sm">
          <div className="flex justify-between items-center gap-2 mb-4">
            <h3 className="text-lg font-semibold text-gray-700">Total Leads by Source</h3>
            <span className="text-sm text-gray-400">
              Total <b className="text-gray-700 text-lg ml-1">{totalLeadCount.toLocaleString()}</b>
            </span>
          </div>
          {leadCounts.length === 0 ? (
            <p className="text-sm text-gray-400">No data for this period.</p>
          ) : (
            <div className="space-y-3">
              {leadCounts.map((item) => (
                <div key={item.name}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }}></span>
                      {item.name}
                    </span>
                    <span className="font-semibold text-gray-700">
                      {item.count.toLocaleString()}{" "}
                      <span className="text-gray-400 font-normal">
                        ({((item.count / totalLeadCount) * 100).toFixed(1)}%)
                      </span>
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-100">
                    <div
                      className="h-2 rounded-full"
                      style={{
                        width: `${(item.count / leadCounts[0].count) * 100}%`,
                        backgroundColor: item.color,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({
  title,
  periodLabel,
  value,
  trend,
  trendText,
  badge,
  isUp,
  icon,
  iconBg,
  iconColor,
}: any) => {
  return (
    <div className="bg-white p-3 rounded-[24px] border border-gray-50 shadow-sm flex flex-col justify-between h-full">
      <div className="flex justify-between items-start mb-4">
        <div className="space-y-1">
          <p className="text-gray-400 text-[16px] font-medium leading-tight mb-3">{title}</p>
          {periodLabel ? <p className="text-[12px] text-gray-500 font-medium">{periodLabel}</p> : null}
          <h2 className="text-gray-800 text-[25px] font-semibold tracking-tight">
            {value}
            {badge ? (
              <span className="ml-2 align-middle text-[13px] font-semibold text-[#004aad] bg-[#EBF2FF] rounded-full px-2 py-0.5">
                {badge}
              </span>
            ) : null}
          </h2>
        </div>
        <div className={`${iconBg} ${iconColor} p-3 rounded-3xl text-4xl flex items-center justify-center`}>
          {icon}
        </div>
      </div>
      <div className="flex items-center gap-2 mt-2">
        {typeof trend === "string" ? (
          <div
            className={`flex items-center gap-2 text-[17px] font-semibold ${
              isUp ? "text-emerald-500" : "text-rose-500"
            }`}
          >
            {isUp ? (
              <RiseOutlined size={18} className="text-[25px]" />
            ) : (
              <FallOutlined size={18} className="text-[25px]" />
            )}
            <span>{trend}</span>
          </div>
        ) : null}
        <span className="text-gray-400 text-[14px] font-normal">{trendText}</span>
      </div>
    </div>
  );
};

export default OtcTrackers;
